// Persistencia local cifrada en IndexedDB.
//
// MODELO DE CONFIDENCIALIDAD (post-refactor 2026-04-29):
//
// 1. VAULT principal (clave 'main' en IndexedDB):
//    - Cifrado con clave derivada del PIN del usuario (Argon2id MODERATE).
//    - Contiene SOLO: session, settings, contactos/grupos/cache de FRECUENCIA GENERAL.
//    - Vault PIN compromise => atacante ve frecuencia general y nada más.
//
// 2. BLOBS PER-FRECUENCIA (claves 'freq_<hmac>' en IndexedDB):
//    - Una entrada por cada frecuencia secreta visitada.
//    - Cifrado con freqKey (Argon2id de la frecuencia, NO con el PIN).
//    - Atacante con vault PIN no puede descifrarlos (necesita la frecuencia).
//    - Sin la frecuencia ni siquiera puede saber a qué frecuencia pertenece cada blob
//      (el storage key es HMAC(freqKey, "vault-freq-id-v1")).
//
// Las frecuencias NO se guardan nunca en claro. Si pierdes la frecuencia,
// pierdes el acceso al historial cacheado de esa frecuencia.

import * as crypto from './crypto';

const DB_NAME = 'ikognit';
const DB_VERSION = 1;
const STORE_NAME = 'vault';
const ENTRY_KEY = 'main';
const FREQ_VAULT_MARKER = 'ikognit-vault-freq-id-v1';

interface VaultRecord {
  salt: string;          // base64 (vault PIN-derived salt)
  ciphertext: string;    // base64 (incluye nonce + cipher)
}

// @ts-expect-error legacy unused
interface FreqBlobRecord {
  // freqKey ya es la clave; no salt ni derivación adicional necesaria.
  ciphertext: string;    // base64 (nonce + cipher)
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE_NAME, mode);
    const store = t.objectStore(STORE_NAME);
    const req = fn(store);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => db.close();
  });
}

export async function hasVault(): Promise<boolean> {
  try {
    const result = await tx<unknown>('readonly', (s) => s.get(ENTRY_KEY) as IDBRequest<unknown>);
    return result !== undefined && result !== null;
  } catch {
    return false;
  }
}

// ============================================================
// Tipos públicos
// ============================================================

export interface SerializedKeyPair {
  publicKey: string;   // base64
  privateKey: string;  // base64
}

export interface SerializedPrekeyMaterial {
  spk: { id: number; pub: string; priv: string; pqPub: string; pqPriv: string };
  opks: Array<{ id: number; pub: string; priv: string; pqPub: string; pqPriv: string }>;
  nextOpkId: number;
}

// Estado serializado de una sesión Double Ratchet (todos los Uint8Array como base64).
export interface SerializedRatchetState {
  rootKey: string;
  sendChainKey: string | null;
  recvChainKey: string | null;
  myRatchetPriv: string | null;
  myRatchetPub: string | null;
  theirRatchetPub: string | null;
  sendCounter: number;
  recvCounter: number;
  prevSendCounter: number;
  skipped: Record<string, string>;              // skipKey → messageKey (ambos b64)
}

export interface VaultData {
  session: {
    ikognitId: string;
    nick?: string;         // Auth v2: handle público visible. Se hidrata en unlock.
    token: string;
    publicKey: string;     // base64
    privateKey: string;    // base64
    // wrappingKey (Argon2id del password) cifrada bajo el PIN del vault.
    // Imprescindible para descifrar archive_chunks tras unlock con PIN
    // (modelo simple v200, 2026-05-04). Sin esto, los salientes desaparecen.
    wrappingKey?: string;  // base64
    // masterVaultKey ESTABLE (clave del archivo/vault, independiente del password),
    // cifrada bajo el PIN igual que wrappingKey. Sin esto, el unlock por PIN usaría
    // wrappingKey y desincronizaría con el login en cuentas con frase.
    masterVaultKey?: string;  // base64
    // DOMINIO de esta sesión. Al restaurarla hay que restaurarlo también: sin la
    // clave del dominio, el dispositivo no puede derivar ni uno de sus IKO y la
    // persona vería todo vacío sin entender por qué.
    dominio?: string | null;
    domainKey?: string | null;   // base64
    owner?: boolean;
  };
  // SOLO frecuencia general. Las frecuencias secretas viven en blobs aparte.
  chats: Record<string, SerializedChat>;       // by contactId
  groups: Record<string, SerializedGroup>;      // by groupId
  pubKeyCache: Record<string, string>;          // by ikognitId — solo contactos de general
  settings?: IKOGNITSettings;
  contactProfiles?: Record<string, ContactProfileEntry>;
  // F1.2e — Identidad firmadora (Ed25519) y material de prekeys (X25519 + Kyber).
  signing?: SerializedKeyPair;
  prekeyMaterial?: SerializedPrekeyMaterial;
  // Sesiones Double Ratchet de la frecuencia general, indexadas por ikognitId del peer.
  ratchetSessions?: Record<string, SerializedRatchetState>;
}

export interface FreqBlobData {
  chats: Record<string, SerializedChat>;
  groups: Record<string, SerializedGroup>;
  pubKeyCache: Record<string, string>;
  contactProfiles: Record<string, ContactProfileEntry>;
  // Sesiones Double Ratchet en esta frecuencia, indexadas por ikognitId del peer.
  ratchetSessions?: Record<string, SerializedRatchetState>;
}

export type Visibility = 'all' | 'contacts' | 'none';

export interface ProfileFields {
  displayName: string;
  photoB64: string | null;
  info: string;
}

export interface PrivacyFields {
  showPhoto: Visibility;
  showInfo: Visibility;
  showLastSeen: Visibility;
  showOnline: Visibility;
  sendReadReceipts: boolean;
  whoAddsMeToGroups: Visibility;
}

export interface NotificationFields {
  sound: boolean;
  preview: boolean;
  vibration: boolean;
}

export interface ContactNote {
  alias?: string;
  notes?: string;
}

export interface IKOGNITSettings {
  profile: ProfileFields;
  profileByFrequency: Record<string, Partial<ProfileFields>>;
  privacy: PrivacyFields;
  privacyByFrequency: Record<string, Partial<PrivacyFields>>;
  messages: { defaultTtlSeconds: number };
  blocked: string[];
  notifications: NotificationFields;
  // rememberFrequencies eliminado: filosofía de IKOGNIT = nunca guardar frecuencias.
  session: { inactivityTimeoutSeconds: number };
  account: { emailOptional: string | null };
  language?: string;
  // Alias y notas privadas por contacto. Solo el usuario las ve (cifradas en el
  // blob de prefs). Server no las ve.
  contactNotes?: Record<string, ContactNote>;
}

export interface ContactProfileEntry {
  displayName: string;
  photoB64: string | null;
  info: string;
  receivedAt: string;
}

export const TTL_OPTIONS = [
  { labelKey: 'ttl.1h', seconds: 60 * 60 },
  { labelKey: 'ttl.1d', seconds: 60 * 60 * 24 },
  { labelKey: 'ttl.1w', seconds: 60 * 60 * 24 * 7 },
  { labelKey: 'ttl.1m', seconds: 60 * 60 * 24 * 30 },
  { labelKey: 'ttl.6m', seconds: 60 * 60 * 24 * 30 * 6 },
  { labelKey: 'ttl.12m', seconds: 60 * 60 * 24 * 30 * 12 },
] as const;

export const TIMEOUT_OPTIONS = [
  { labelKey: 'timeout.5min', seconds: 60 * 5 },
  { labelKey: 'timeout.15min', seconds: 60 * 15 },
  { labelKey: 'timeout.30min', seconds: 60 * 30 },
  { labelKey: 'timeout.1h', seconds: 60 * 60 },
  { labelKey: 'timeout.never', seconds: 0 },
] as const;

export function defaultSettings(): IKOGNITSettings {
  return {
    profile: { displayName: '', photoB64: null, info: '' },
    profileByFrequency: {},
    privacy: {
      showPhoto: 'contacts',
      showInfo: 'contacts',
      showLastSeen: 'contacts',
      showOnline: 'contacts',
      sendReadReceipts: true,
      whoAddsMeToGroups: 'contacts',
    },
    privacyByFrequency: {},
    messages: { defaultTtlSeconds: 60 * 60 * 24 * 30 * 12 },
    blocked: [],
    notifications: { sound: true, preview: true, vibration: true },
    session: { inactivityTimeoutSeconds: 60 * 15 },
    account: { emailOptional: null },
    contactNotes: {},
  };
}

export function effectiveProfile(settings: IKOGNITSettings, freqName: string | null): ProfileFields {
  const base = settings.profile;
  if (!freqName) return base;
  const override = settings.profileByFrequency[freqName] ?? {};
  return { ...base, ...override };
}

export function effectivePrivacy(settings: IKOGNITSettings, freqName: string | null): PrivacyFields {
  const base = settings.privacy;
  if (!freqName) return base;
  const override = settings.privacyByFrequency[freqName] ?? {};
  return { ...base, ...override };
}

export interface SerializedChat {
  contactId: string;
  contactPublicKey: string; // base64
  messages: SerializedMessage[];
  confirmed?: boolean;
}

export interface SerializedGroup {
  id: string;
  name: string;
  members: Array<{ ikognitId: string; publicKey: string }>;
  messages: SerializedMessage[];
}

export type SerializedMessage =
  | (SerializedBaseMessage & { kind: 'text'; text: string })
  | (SerializedBaseMessage & {
      kind: 'image' | 'video' | 'audio' | 'file';
      mediaId: string;
      fileKey: string; // base64
      name: string;
      size: number;
      contentType: string;
      voice?: boolean;
      duration?: number;
      peaks?: number[];
    })
  | (SerializedBaseMessage & { kind: 'contact'; contactIKOGNITId: string })
  | (SerializedBaseMessage & {
      kind: 'location';
      lat: number;
      lng: number;
      accuracy?: number;
      label?: string;
    })
  | (SerializedBaseMessage & {
      kind: 'location_live';
      shareId: string;
      startedAt: string;
      durationSeconds: number;
      lat: number;
      lng: number;
      accuracy?: number;
      lastUpdateAt?: string;
      endedAt?: string;
    });

export interface SerializedBaseMessage {
  id: string;
  fromMe: boolean;
  at: string;
  deliveredAt?: string | null;
  readAt?: string | null;
  fromIKOGNITId?: string;
}

// ============================================================
// Vault principal (PIN-protected)
// ============================================================

export async function saveVault(pin: string, data: VaultData): Promise<void> {
  await crypto.ready();
  let salt: Uint8Array;
  const existing = (await tx<VaultRecord | undefined>('readonly', (s) => s.get(ENTRY_KEY) as IDBRequest<VaultRecord | undefined>)) as VaultRecord | undefined;
  if (existing && existing.salt) {
    salt = crypto.b64decode(existing.salt);
  } else {
    salt = crypto.newSalt();
  }
  const key = await crypto.deriveKeyFromPassword(pin, salt);
  const json = JSON.stringify(data);
  const wrapped = crypto.wrapPrivateKey(new TextEncoder().encode(json), key);
  const record: VaultRecord = {
    salt: crypto.b64encode(salt),
    ciphertext: crypto.b64encode(wrapped.ciphertext),
  };
  await tx<IDBValidKey>('readwrite', (s) => s.put(record, ENTRY_KEY));
}

// Migra silenciosamente vaults legacy: si v.chats viene como
// Record<freqName, Record<contactId, SerializedChat>>, devolvemos solo el contenido
// de '__general__' (las demás frecuencias se descartan, hay que re-sintonizar).
function migrateLegacyVault(raw: any): VaultData {
  const out: VaultData = {
    session: raw.session,
    chats: {},
    groups: {},
    pubKeyCache: raw.pubKeyCache ?? {},
    settings: raw.settings,
    contactProfiles: raw.contactProfiles,
  };
  // Normalizar settings: quitar el campo legacy rememberFrequencies si existe.
  if (out.settings && (out.settings.session as any)?.rememberFrequencies !== undefined) {
    const { rememberFrequencies: _drop, ...rest } = out.settings.session as any;
    out.settings = { ...out.settings, session: rest } as IKOGNITSettings;
  }
  // chats: detectar formato legacy (anidado por freq) vs nuevo (plano por contactId).
  const chatsRaw = raw.chats ?? {};
  const looksLegacy =
    chatsRaw.__general__ !== undefined ||
    Object.keys(chatsRaw).some((k) => {
      const v = chatsRaw[k];
      return v && typeof v === 'object' && !('messages' in v) && !('contactPublicKey' in v);
    });
  if (looksLegacy) {
    const general = chatsRaw.__general__ ?? {};
    out.chats = (typeof general === 'object' && general !== null) ? general : {};
  } else {
    out.chats = chatsRaw;
  }
  // groups: mismo análisis.
  const groupsRaw = raw.groups ?? {};
  const groupsLegacy =
    groupsRaw.__general__ !== undefined ||
    Object.keys(groupsRaw).some((k) => {
      const v = groupsRaw[k];
      return v && typeof v === 'object' && !('messages' in v) && !('id' in v);
    });
  if (groupsLegacy) {
    const general = groupsRaw.__general__ ?? {};
    out.groups = (typeof general === 'object' && general !== null) ? general : {};
  } else {
    out.groups = groupsRaw;
  }
  return out;
}

export async function loadVault(pin: string): Promise<VaultData | null> {
  await crypto.ready();
  const record = (await tx<VaultRecord | undefined>('readonly', (s) => s.get(ENTRY_KEY) as IDBRequest<VaultRecord | undefined>)) as VaultRecord | undefined;
  if (!record) return null;
  try {
    const salt = crypto.b64decode(record.salt);
    const key = await crypto.deriveKeyFromPassword(pin, salt);
    const plainBytes = crypto.unwrapPrivateKey(crypto.b64decode(record.ciphertext), key);
    const json = new TextDecoder().decode(plainBytes);
    const raw = JSON.parse(json);
    return migrateLegacyVault(raw);
  } catch {
    return null;
  }
}

export async function clearVault(): Promise<void> {
  // Borra el vault principal Y todos los blobs per-frecuencia.
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(STORE_NAME, 'readwrite');
    const store = t.objectStore(STORE_NAME);
    const req = store.getAllKeys();
    req.onsuccess = () => {
      const keys = (req.result as IDBValidKey[]) ?? [];
      for (const k of keys) {
        if (typeof k === 'string' && (k === ENTRY_KEY || k.startsWith('freq_'))) {
          store.delete(k);
        }
      }
    };
    t.oncomplete = () => { db.close(); resolve(); };
    t.onerror = () => { db.close(); reject(t.error); };
  });
  // La frase BIP39 vive en localStorage bajo una key fija (no per-user). Si no
  // la borramos aquí, una nueva cuenta hereda el blob y al intentar descifrar
  // con su wrappingKey distinta da "decrypt_failed" eternamente.
  try { localStorage.removeItem('ikognit_recovery_phrase_enc_v1'); } catch {}
}

// ============================================================
// Blobs per-frecuencia (freqKey-protected)
// ============================================================

function freqStorageKey(freqKey: Uint8Array): string {
  const tag = crypto.hmacTag(freqKey, FREQ_VAULT_MARKER);
  return 'freq_' + crypto.b64encode(tag);
}

// Modelo simple (2026-05-04): las frecuencias NO se persisten en NINGÚN lado.
// Cada vez que el usuario quiera entrar en una freq, re-introduce el secreto;
// la pseudo-cuenta se materializa en RAM y el archive cifrado vive en server.
// Si pierdes el secreto, se pierde la conversación — landing cumplida.

export async function saveFreqBlob(_freqKey: Uint8Array, _data: FreqBlobData): Promise<void> {
  /* no-op: freqs solo en RAM */
  void _freqKey; void _data; void freqStorageKey;
}

export async function loadFreqBlob(_freqKey: Uint8Array): Promise<FreqBlobData | null> {
  return null;
}

export async function deleteFreqBlob(freqKey: Uint8Array): Promise<void> {
  // Mantenido como hard-delete por si quedaron blobs legacy en IDB de versiones anteriores.
  const key = freqStorageKey(freqKey);
  try { await tx<undefined>('readwrite', (s) => s.delete(key) as IDBRequest<undefined>); } catch (_e) { /* ignore */ }
}
